import React, { useEffect, useRef, useState } from 'react';
import Toastify, { notifyError, notifySuccess } from "../../../utils/tostfy";
import MasterSuccessModal from '../MasterSuccessModal';
import { Link } from 'react-router-dom';
import { handleApiCall } from '../../../apis';
import { endpoint } from '../../../utils/endpoints';
import EditIcon from '../../../assets/image/edit-icon.svg'
import DeleteIcon from '../../../assets/image/delete-icon.svg'
import EyeIcon from '../../../assets/image/eye-icon.svg'
import LeftAngle from '../../../assets/image/left-angle.svg'
import RightAngle from '../../../assets/image/right-angle.svg'
import PermissionGate from '../../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../../utils/permissions';
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../../utils/search";
import { SingleDatePanel } from '../../../Components/SingleDatePicker/SingleDatePicker';
import { parseApiDate, toApiDate } from '../../../utils/dateRange';
import PageNumberInput from "../../../Components/Pagination/PageNumberInput";

const FUEL_PUMP_NAME_MAX_LENGTH = 120;
const normalizeFuelPumpNameInput = (value) =>
    String(value ?? '')
        .replace(/[^A-Za-z0-9 ]/g, '')
        .replace(/\s{2,}/g, ' ')
        .slice(0, FUEL_PUMP_NAME_MAX_LENGTH);

const isValidFuelPumpName = (value) => {
    const name = String(value || '').trim();
    return /^[A-Za-z0-9 ]+$/.test(name) && /[A-Za-z]/.test(name);
};

const formatEditDateLabel = (value) => {
    const date = parseApiDate(value);
    if (!date) return 'dd/mm/yyyy';
    return date.toLocaleDateString('en-GB');
};

const FuelPumpMaster = () => {
    const [fuelPumps, setFuelPumps] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [addFuelPump, setAddFuelPump] = useState(null);
    const [editFuelPump, setEditFuelPump] = useState(null);
    const [miplLocations, setMiplLocations] = useState([]);
    const [loadingMiplLocations, setLoadingMiplLocations] = useState(false);
    const [fuelCompanies, setFuelCompanies] = useState([]);
    const [loadingFuelCompanies, setLoadingFuelCompanies] = useState(false);
    const [selectedMiplLocationId, setSelectedMiplLocationId] = useState('');
    const [fuelPumpName, setFuelPumpName] = useState('');
    const [fuelRate, setFuelRate] = useState('');
    const [address, setAddress] = useState('');
    const [selectedFuelCompanyId, setSelectedFuelCompanyId] = useState('');
    const [savingFuelPump, setSavingFuelPump] = useState(false);
    const [addErrors, setAddErrors] = useState({
        mipl_location_id: '',
        fuel_pump_company_id: '',
        fuel_pump_name: '',
        fuel_rate: '',
        address: '',
    });
    const [editFuelRate, setEditFuelRate] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [editEffectiveFrom, setEditEffectiveFrom] = useState('');
    const [editDatePickerOpen, setEditDatePickerOpen] = useState(false);
    const editDatePickerRef = useRef(null);
    const [editFuelCompanyId, setEditFuelCompanyId] = useState('');
    const [editErrors, setEditErrors] = useState({
        fuel_pump_company_id: '',
        fuel_rate: '',
        effective_from: '',
        address: '',
    });
    const [savingEditFuelPump, setSavingEditFuelPump] = useState(false);

    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [statusUpdatingId, setStatusUpdatingId] = useState('');
    const [successModal, setSuccessModal] = useState({ open: false, message: '' });
    const openMasterSuccess = (message) =>
        setSuccessModal({ open: true, message: message || 'Saved successfully' });
    const closeMasterSuccess = () => setSuccessModal({ open: false, message: '' });

    const mapFuelPump = (item) => {
        const companyRef = item?.fuel_pump_company_id ?? item?.company_id ?? item?.fuel_company;
        const fuelCompanyId =
            typeof companyRef === 'object' && companyRef !== null
                ? companyRef._id || companyRef.id || ''
                : companyRef || '';
        const fuelCompanyName =
            typeof companyRef === 'object' && companyRef?.name != null
                ? companyRef.name
                : item?.fuel_company_name || item?.company_name || '-';

        return {
            id: item?._id || item?.id || '',
            fuelPumpName: item?.fuel_pump_name || item?.name || '-',
            fuelRate: item?.fuel_rate ?? '-',
            miplLocation: item?.mipl_location_id?.name || item?.mipl_location?.name || '-',
            fuelCompanyId,
            fuelCompanyName,
            address: item?.address != null && item?.address !== '' ? String(item.address) : '',
            statusCode: item?.status === 1 ? 1 : 0,
            status: item?.status === 1 ? 'Active' : 'Inactive',
        };
    };
    const searchForApi = (() => {
        const s = clampSearchInput(debouncedSearch).trim();
        if (!s) return '';
        if (/^\+?[\d\s-]+$/.test(s)) return s.replace(/\D/g, '');
        return s;
    })();
    const fetchFuelPumpList = async () => {
        setListLoading(true);
        try {
            const response = await handleApiCall(
                'get',
                `${endpoint.listOfFuelPump}?search=${encodeURIComponent(searchForApi)}&page=${currentPage}&limit=${itemsPerPage}`,
                {}
            );
            if (response?.data?.code === 200) {
                const d = response?.data?.data || {};
                const raw = d?.fuel_pump_data || d?.fuelPump_data || d?.fuel_pumps || d;
                const mapped = (Array.isArray(raw) ? raw : []).map(mapFuelPump);
                setFuelPumps(mapped);
                setTotalRecords(d?.total ?? mapped.length);
                setTotalPages(Math.max(1, d?.total_pages ?? 1));
            } else {
                const errMsg =
                    response?.response?.data?.errorMessage ||
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    'Failed to load fuel pumps';
                notifyError(errMsg);
            }
        } catch {
            notifyError('Failed to load fuel pumps');
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        fetchFuelPumpList();
    }, [debouncedSearch, currentPage, itemsPerPage]);

    useEffect(() => {
        if (!editDatePickerOpen) return undefined;
        const handleClickOutside = (event) => {
            if (editDatePickerRef.current && !editDatePickerRef.current.contains(event.target)) {
                setEditDatePickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [editDatePickerOpen]);

    const handleFuelPumpStatusToggle = async (fuelPumpId, currentStatus) => {
        const nextStatus = currentStatus === 1 ? 0 : 1;
        if (!fuelPumpId) return;
        setStatusUpdatingId(fuelPumpId);
        try {
            const response = await handleApiCall('put', endpoint.updateStatusFuelPump, {
                fuel_pump_id: fuelPumpId,
                status: nextStatus,
            });
            if (response?.data?.code === 200) {
                setFuelPumps((prev) =>
                    prev.map((fp) =>
                        fp.id === fuelPumpId
                            ? {
                                  ...fp,
                                  statusCode: nextStatus,
                                  status: nextStatus === 1 ? 'Active' : 'Inactive',
                              }
                            : fp
                    )
                );
                notifySuccess(response?.data?.message || 'Fuel pump status updated');
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Could not update fuel pump status';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setStatusUpdatingId('');
        }
    };

    const toArray = (value) => {
        if (Array.isArray(value)) return value;
        if (value && Array.isArray(value.data)) return value.data;
        return [];
    };

    const mapLocation = (item) => ({
        id: item?._id || item?.id || '',
        name: item?.name || item?.location_name || 'Unknown Location',
    });

    const mapFuelCompany = (item) => ({
        id: item?._id || item?.id || '',
        name: item?.name || '-',
    });

    const resetAddFuelPumpForm = () => {
        setSelectedMiplLocationId('');
        setSelectedFuelCompanyId('');
        setFuelPumpName('');
        setFuelRate('');
        setAddress('');
        setAddErrors({
            mipl_location_id: '',
            fuel_pump_company_id: '',
            fuel_pump_name: '',
            fuel_rate: '',
            address: '',
        });
    };

    const fetchAllMiplLocations = async () => {
        setLoadingMiplLocations(true);
        try {
            const response = await handleApiCall('get', endpoint.listOfAlllocation, {});
            if (response?.data?.code === 200) {
                const d = response?.data?.data;
                // API may return data as a top-level array or as { location_data: [...] }.
                // Do not use [] || x — [] is truthy and blocks the fallback.
                const raw = Array.isArray(d) ? d : toArray(d?.location_data);
                const mapped = raw.map(mapLocation).filter((x) => x.id && x.name);
                setMiplLocations(mapped);
            } else {
                const errMsg =
                    response?.response?.data?.errorMessage ||
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    'Failed to load MIPL locations';
                notifyError(errMsg);
            }
        } catch {
            notifyError('Failed to load MIPL locations');
        } finally {
            setLoadingMiplLocations(false);
        }
    };

    const fetchFuelPumpCompanies = async () => {
        setLoadingFuelCompanies(true);
        try {
            const response = await handleApiCall('get', endpoint.listOfFuelPumpCompany, {});
            if (response?.data?.code === 200) {
                const d = response?.data?.data;
                const raw = Array.isArray(d) ? d : toArray(d);
                const mapped = raw.map(mapFuelCompany).filter((x) => x.id && x.name);
                setFuelCompanies(mapped);
            } else {
                const errMsg =
                    response?.response?.data?.errorMessage ||
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    'Failed to load fuel companies';
                notifyError(errMsg);
                setFuelCompanies([]);
            }
        } catch {
            notifyError('Failed to load fuel companies');
            setFuelCompanies([]);
        } finally {
            setLoadingFuelCompanies(false);
        }
    };

    const openAddFuelPump = async () => {
        resetAddFuelPumpForm();
        setAddFuelPump(true);
        await Promise.all([fetchAllMiplLocations(), fetchFuelPumpCompanies()]);
    };

    const closeAddFuelPump = () => {
        setAddFuelPump(false);
        resetAddFuelPumpForm();
    };

    const resetEditFuelPumpForm = () => {
        setEditFuelRate('');
        setEditAddress('');
        setEditEffectiveFrom('');
        setEditFuelCompanyId('');
        setEditErrors({ fuel_pump_company_id: '', fuel_rate: '', effective_from: '', address: '' });
    };

    const openEditFuelPump = async (row) => {
        setEditFuelPump(row);
        setEditFuelRate(row?.fuelRate != null && row?.fuelRate !== '-' ? `${row.fuelRate}` : '');
        setEditAddress(row?.address != null ? String(row.address) : '');
        setEditEffectiveFrom('');
        setEditFuelCompanyId(row?.fuelCompanyId ? String(row.fuelCompanyId) : '');
        setEditErrors({ fuel_pump_company_id: '', fuel_rate: '', effective_from: '', address: '' });
        await fetchFuelPumpCompanies();
    };

    const closeEditFuelPump = () => {
        setEditFuelPump(null);
        resetEditFuelPumpForm();
    };

    const sanitizeDecimalInput = (value, maxDecimals = 2) => {
        let next = `${value}`.replace(/[^\d.]/g, '');
        const dotIdx = next.indexOf('.');
        if (dotIdx !== -1) {
            next =
                next.slice(0, dotIdx + 1) +
                next
                    .slice(dotIdx + 1)
                    .replace(/\./g, '');
            const [intPart, decPart = ''] = next.split('.');
            next = `${intPart}.${decPart.slice(0, maxDecimals)}`;
        }
        return next;
    };

    const validateAddFuelPump = () => {
        const e = {
            mipl_location_id: '',
            fuel_pump_company_id: '',
            fuel_pump_name: '',
            fuel_rate: '',
            address: '',
        };
        const name = (fuelPumpName || '').trim();
        const rateRaw = (fuelRate || '').trim();
        const rate = Number(rateRaw);
        const addr = (address || '').trim();

        if (!selectedMiplLocationId) e.mipl_location_id = 'MIPL location is required';
        if (!selectedFuelCompanyId) e.fuel_pump_company_id = 'Fuel company is required';
        if (!name) e.fuel_pump_name = 'Fuel pump name is required';
        else if (name.length < 2) e.fuel_pump_name = 'Fuel pump name must be at least 2 characters';
        else if (name.length > FUEL_PUMP_NAME_MAX_LENGTH) e.fuel_pump_name = 'Fuel pump name is too long';
        else if (!isValidFuelPumpName(name)) {
            e.fuel_pump_name = 'Fuel pump name must be alphanumeric and include at least one alphabet.';
        }

        if (!rateRaw) e.fuel_rate = 'Fuel rate is required';
        else if (Number.isNaN(rate) || rate <= 0) e.fuel_rate = 'Fuel rate must be greater than 0';
        else if (!/^\d+(\.\d{1,2})?$/.test(rateRaw)) e.fuel_rate = 'Fuel rate can have up to 2 decimal places';

        if (!addr) e.address = 'Address is required';
        else if (addr.length < 5) e.address = 'Address must be at least 5 characters';
        else if (addr.length > 500) e.address = 'Address is too long (max 500 characters)';

        return {
            errors: e,
            payload: {
                mipl_location_id: selectedMiplLocationId,
                fuel_pump_company_id: selectedFuelCompanyId,
                fuel_pump_name: name,
                fuel_rate: rate,
                address: addr,
            },
        };
    };

    const handleAddFuelPumpSubmit = async (e) => {
        e.preventDefault();
        const { errors, payload } = validateAddFuelPump();
        setAddErrors(errors);
        if (
            errors.mipl_location_id ||
            errors.fuel_pump_company_id ||
            errors.fuel_pump_name ||
            errors.fuel_rate ||
            errors.address
        )
            return;

        setSavingFuelPump(true);
        try {
            const response = await handleApiCall('post', endpoint.addFuelPump, payload);
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Fuel pump added successfully');
                closeAddFuelPump();
                if (currentPage !== 1) setCurrentPage(1);
                else await fetchFuelPumpList();
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Failed to add fuel pump';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingFuelPump(false);
        }
    };

    const validateEditFuelPump = () => {
        const e = { fuel_pump_company_id: '', fuel_rate: '', effective_from: '', address: '' };
        const rateRaw = (editFuelRate || '').trim();
        const rate = Number(rateRaw);
        const addr = (editAddress || '').trim();

        if (!editFuelCompanyId) e.fuel_pump_company_id = 'Fuel company is required';

        if (!rateRaw) e.fuel_rate = 'Fuel Rate Update Required';
        else if (Number.isNaN(rate) || rate <= 0) e.fuel_rate = 'Fuel rate must be greater than 0';
        else if (!/^\d+(\.\d{1,2})?$/.test(rateRaw)) e.fuel_rate = 'Fuel rate can have up to 2 decimal places';

        if (!editEffectiveFrom) e.effective_from = 'Date is required';
        const effectiveTimestamp = editEffectiveFrom ? new Date(editEffectiveFrom).getTime() : NaN;
        if (editEffectiveFrom && Number.isNaN(effectiveTimestamp)) {
            e.effective_from = 'Enter a valid effective date';
        }

        if (!addr) e.address = 'Address is required';
        else if (addr.length < 5) e.address = 'Address must be at least 5 characters';
        else if (addr.length > 500) e.address = 'Address is too long (max 500 characters)';

        return {
            errors: e,
            payload: {
                fuel_pump_id: editFuelPump?.id,
                fuel_pump_company_id: editFuelCompanyId,
                effective_from: effectiveTimestamp,
                fuel_rate: rate,
                address: addr,
            },
        };
    };

    const handleEditFuelPumpSubmit = async (e) => {
        e.preventDefault();
        const { errors, payload } = validateEditFuelPump();
        setEditErrors(errors);
        if (errors.fuel_pump_company_id || errors.fuel_rate || errors.effective_from || errors.address) return;
        if (!payload.fuel_pump_id) {
            notifyError('Fuel pump id is missing');
            return;
        }

        setSavingEditFuelPump(true);
        try {
            const response = await handleApiCall('put', endpoint.editFuelPump, payload);
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Fuel pump updated successfully');
                closeEditFuelPump();
                await fetchFuelPumpList();
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Failed to update fuel pump';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingEditFuelPump(false);
        }
    };

   
    const displayFrom = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

    return (
        <>
        <Toastify/>
            <div className="tab-pane-content md:mt-7 mt-3">

                <div className='flex gap-x-2.5 md:mb-4 mb-3'>
                    
                    <input
                        type="text"
                        placeholder="Search..."
                        className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
                        value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                    />

                    <PermissionGate module={MODULES.MASTER} action="add">
                        <button onClick={openAddFuelPump} className="btn btn-danger ml-auto">
                            + Add New
                        </button>
                    </PermissionGate>
                </div>

                {/* Table */}
                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Fuel Pump Name</th>
                                    <th>Fuel Rate</th>
                                    <th>MIPL Location</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody className='max-[767px]:[&_td]:!py-2'>
                                {listLoading && (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                )}
                                {!listLoading && fuelPumps.map((driver, index) => (
                                    <tr key={driver.id}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{driver.fuelPumpName}</td>
                                        <td>{driver.fuelRate}</td>
                                        <td>{driver.miplLocation}</td>
                                        <td>{driver.status}</td>

                                        {/* Actions */}
                                        <td>
                                            <div className="flex items-center justify-center gap-2">
                                                <PermissionGate module={MODULES.MASTER} action="edit">
                                                    <label className="relative inline-flex items-center cursor-pointer w-9 min-w-9 h-5 bg-[#E11818] rounded-full my-0 [&:has(input:checked)]:bg-[#17B517] [&:has(input:checked)_.absolute]:!left-[17px] transition-all">
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={driver.statusCode === 1}
                                                            onChange={() => handleFuelPumpStatusToggle(driver.id, driver.statusCode)}
                                                            disabled={statusUpdatingId === driver.id || listLoading}
                                                        />
                                                        <div className="absolute left-[3px] top-0.5 w-4 h-4 bg-white rounded-full transition-all"></div>
                                                    </label>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.MASTER} action="edit">
                                                    <span onClick={() => openEditFuelPump(driver)}><img src={EditIcon} className='h-7 min-w-[22px]'/></span>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.MASTER} action="view">
                                                    <Link to={`/fuel-pump-master/view-details?fuel_pump_id=${encodeURIComponent(driver.id)}`}><img src={EyeIcon} className='h-7 min-w-[22px]'/></Link>
                                                </PermissionGate>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!listLoading && fuelPumps.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-gray-500">
                                            No fuel pumps found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 📄 Pagination */}
                <div className="flex gap-x-4 items-center md:mt-4 mt-3">

                    <select
                        className='p-2 shadow-sm outline-none max-[767px]:text-xs'
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    {/* Total Records */}
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {listLoading
                            ? '—'
                            : totalRecords === 0
                                ? '0 records found'
                                : `${displayFrom}-${displayTo} of ${totalRecords} records found`}
                    </div>
                    {/* Pagination Controls */}
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type='button'
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={listLoading || currentPage === 1}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={LeftAngle} className='invert grayscale brightness-100'/>
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">
                            of {totalPages}
                        </span>
                        <button
                            type='button'
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={listLoading || currentPage === totalPages || totalRecords === 0}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={RightAngle} className='invert grayscale brightness-100'/>
                        </button>

                    </div>
                </div>

            </div>

            {/* Add New Fuel Pump Modal */}
            {addFuelPump && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] max-[767px]:px-4">
                    <div className="bg-white p-6 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h2 className="mb-4 text-center !text-xl font-UbuntuBold">Add Fuel Pump</h2>
                        <form
                            className='font-UbuntuRegular [&_label]:mb-1.5 [&_label]:font-UbuntuMedium [&_.form-control]:shadow-none'
                            onSubmit={handleAddFuelPumpSubmit}
                            noValidate
                        >
                           
                            <div className='form-group mb-3'>
                                <label>MIPL Location</label>
                                <select
                                    className='form-control'
                                    value={selectedMiplLocationId}
                                    onChange={(e) => {
                                        setSelectedMiplLocationId(e.target.value);
                                        if (addErrors.mipl_location_id) {
                                            setAddErrors((prev) => ({ ...prev, mipl_location_id: '' }));
                                        }
                                    }}
                                    disabled={loadingMiplLocations || savingFuelPump}
                                >
                                    <option value="">
                                        {loadingMiplLocations ? 'Loading MIPL locations...' : 'Select MIPL Location'}
                                    </option>
                                    {miplLocations.map((loc) => (
                                        <option key={loc.id} value={loc.id}>
                                            {loc.name}
                                        </option>
                                    ))}
                                </select>
                                {addErrors.mipl_location_id && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.mipl_location_id}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label>Fuel Company</label>
                                <select
                                    className='form-control'
                                    value={selectedFuelCompanyId}
                                    onChange={(e) => {
                                        setSelectedFuelCompanyId(e.target.value);
                                        if (addErrors.fuel_pump_company_id) {
                                            setAddErrors((prev) => ({ ...prev, fuel_pump_company_id: '' }));
                                        }
                                    }}
                                    disabled={loadingFuelCompanies || savingFuelPump}
                                >
                                    <option value="">
                                        {loadingFuelCompanies ? 'Loading fuel companies...' : 'Select fuel company'}
                                    </option>
                                    {fuelCompanies.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                                {addErrors.fuel_pump_company_id && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.fuel_pump_company_id}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label>Fuel Pump Name</label>
                                <input
                                    className='form-control'
                                    value={fuelPumpName}
                                    onChange={(e) => {
                                        setFuelPumpName(normalizeFuelPumpNameInput(e.target.value));
                                        if (addErrors.fuel_pump_name) {
                                            setAddErrors((prev) => ({ ...prev, fuel_pump_name: '' }));
                                        }
                                    }}
                                    maxLength={FUEL_PUMP_NAME_MAX_LENGTH}
                                    title="Use letters and numbers. Name cannot be only numeric."
                                    disabled={savingFuelPump}
                                />
                                {addErrors.fuel_pump_name && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.fuel_pump_name}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label>Fuel Rate</label>
                                <input
                                    className='form-control'
                                    inputMode='decimal'
                                    value={fuelRate}
                                    onChange={(e) => {
                                        setFuelRate(sanitizeDecimalInput(e.target.value, 2));
                                        if (addErrors.fuel_rate) {
                                            setAddErrors((prev) => ({ ...prev, fuel_rate: '' }));
                                        }
                                    }}
                                    disabled={savingFuelPump}
                                />
                                {addErrors.fuel_rate && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.fuel_rate}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label>Address</label>
                                <textarea
                                    className='form-control min-h-[88px] resize-y'
                                    rows={3}
                                    maxLength={500}
                                    value={address}
                                    placeholder='Enter full address'
                                    onChange={(e) => {
                                        setAddress(e.target.value);
                                        if (addErrors.address) {
                                            setAddErrors((prev) => ({ ...prev, address: '' }));
                                        }
                                    }}
                                    disabled={savingFuelPump}
                                />
                                {addErrors.address && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.address}</p>
                                )}
                            </div>
                            <div className="flex justify-center gap-3 mt-3">
                                <button
                                    type='button'
                                    className="btn btn-primary flex-1 max-[767px]:!py-2.5"
                                    onClick={closeAddFuelPump}
                                    disabled={savingFuelPump}
                                >
                                    Cancel
                                </button>

                                <button
                                    type='submit'
                                    className="btn btn-danger flex-1 max-[767px]:!py-2.5"
                                    disabled={savingFuelPump}
                                >
                                    {savingFuelPump ? 'Adding...' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Copmany Modal */}
            {editFuelPump && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] max-[767px]:px-4">
                    <div className="bg-white p-6 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h2 className="mb-4 text-center !text-xl font-UbuntuBold">Edit Fuel Pump</h2>
                        <form
                            className='font-UbuntuRegular [&_label]:mb-1.5 [&_label]:font-UbuntuMedium [&_.form-control]:shadow-none'
                            onSubmit={handleEditFuelPumpSubmit}
                            noValidate
                        >
                            <div className='form-group mb-3'>
                                <label>Fuel Pump Name</label>
                                <input className='form-control bg-light' value={editFuelPump?.fuelPumpName || ''} readOnly />
                            </div>
                            <div className='form-group mb-3'>
                                <label>MIPL Location</label>
                                <input className='form-control bg-light' value={editFuelPump?.miplLocation || ''} readOnly />
                            </div>
                            <div className='form-group mb-3'>
                                <label>Fuel Company</label>
                                <input className='form-control bg-light' value={editFuelPump?.fuelCompanyName || ''} readOnly />
                                {/* <select
                                    className='form-control'
                                    value={editFuelCompanyId}
                                    
                                    onChange={(e) => {
                                        setEditFuelCompanyId(e.target.value);
                                        if (editErrors.fuel_pump_company_id) {
                                            setEditErrors((prev) => ({ ...prev, fuel_pump_company_id: '' }));
                                        }
                                    }}
                                    disabled={loadingFuelCompanies || savingEditFuelPump}
                                >
                                    <option value="">
                                        {loadingFuelCompanies ? 'Loading fuel companies...' : 'Select fuel company'}
                                    </option>
                                    {fuelCompanies.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select> */}
                                {editErrors.fuel_pump_company_id && (
                                    <p className='text-danger small mt-1 mb-0'>{editErrors.fuel_pump_company_id}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label>Address</label>
                                <textarea
                                    className='form-control min-h-[88px] resize-y'
                                    rows={3}
                                    maxLength={500}
                                    value={editAddress}
                                    placeholder='Enter full address'
                                    onChange={(e) => {
                                        setEditAddress(e.target.value);
                                        if (editErrors.address) {
                                            setEditErrors((prev) => ({ ...prev, address: '' }));
                                        }
                                    }}
                                    disabled={savingEditFuelPump}
                                />
                                {editErrors.address && (
                                    <p className='text-danger small mt-1 mb-0'>{editErrors.address}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label>Fuel Rate</label>
                                <input
                                    className='form-control'
                                    inputMode='decimal'
                                    value={editFuelRate}
                                    onChange={(e) => {
                                        setEditFuelRate(sanitizeDecimalInput(e.target.value, 2));
                                        if (editErrors.fuel_rate) {
                                            setEditErrors((prev) => ({ ...prev, fuel_rate: '' }));
                                        }
                                    }}
                                    disabled={savingEditFuelPump}
                                />
                                {editErrors.fuel_rate && (
                                    <p className='text-danger small mt-1 mb-0'>{editErrors.fuel_rate}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label>Select Date</label>
                                <div className='fuel-pump-edit-date-picker relative' ref={editDatePickerRef}>
                                    <button
                                        type='button'
                                        className='form-control flex items-center justify-between text-left'
                                        onClick={() => !savingEditFuelPump && setEditDatePickerOpen((open) => !open)}
                                        disabled={savingEditFuelPump}
                                    >
                                        <span className={editEffectiveFrom ? 'text-gray-800' : 'text-gray-500'}>
                                            {formatEditDateLabel(editEffectiveFrom)}
                                        </span>
                                        <svg
                                            width='16'
                                            height='16'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                            xmlns='http://www.w3.org/2000/svg'
                                            aria-hidden='true'
                                        >
                                            <path d='M7 2V5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                                            <path d='M17 2V5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                                            <path d='M3.5 9H20.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                                            <path
                                                d='M5 4H19C20.1046 4 21 4.89543 21 6V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V6C3 4.89543 3.89543 4 5 4Z'
                                                stroke='currentColor'
                                                strokeWidth='2'
                                            />
                                        </svg>
                                    </button>
                                    {editDatePickerOpen && !savingEditFuelPump ? (
                                        <div className='absolute bottom-[calc(100%+8px)] left-0 z-[10001] rounded-lg border bg-white p-3 shadow-lg'>
                                            <SingleDatePanel
                                                selected={parseApiDate(editEffectiveFrom)}
                                                minDate={new Date()}
                                                onChange={(date) => {
                                                    setEditEffectiveFrom(toApiDate(date));
                                                    setEditFuelRate('');
                                                    setEditDatePickerOpen(false);
                                                    setEditErrors((prev) => ({
                                                        ...prev,
                                                        effective_from: '',
                                                        fuel_rate: '',
                                                    }));
                                                }}
                                            />
                                            {editEffectiveFrom ? (
                                                <div className='mt-3 border-t border-gray-100 pt-3'>
                                                    <button
                                                        type='button'
                                                        className='w-full rounded border px-3 py-1.5 text-xs text-primary hover:bg-gray-50'
                                                        onClick={() => {
                                                            setEditEffectiveFrom('');
                                                            setEditDatePickerOpen(false);
                                                        }}
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                                {editErrors.effective_from && (
                                    <p className='text-danger small mt-1 mb-0'>{editErrors.effective_from}</p>
                                )}
                            </div>
                            <div className="flex justify-center gap-3 mt-3">
                                <button
                                    type='button'
                                    className="btn btn-primary flex-1 max-[767px]:!py-2.5"
                                    onClick={closeEditFuelPump}
                                    disabled={savingEditFuelPump}
                                >
                                    Cancel
                                </button>

                                <button
                                    type='submit'
                                    className="btn btn-danger flex-1 max-[767px]:!py-2.5"
                                    disabled={savingEditFuelPump}
                                >
                                    {savingEditFuelPump ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <MasterSuccessModal
                open={successModal.open}
                message={successModal.message}
                onClose={closeMasterSuccess}
            />
        </>
    );
};

export default FuelPumpMaster;